import React, { useState, useMemo } from 'react';

// Avatar
const COLORS = ['#7C3AED', '#2563EB', '#059669', '#D97706', '#DC2626', '#0891B2'];
export const Avatar = ({ name, size = 26 }) => {
  const initials = (name || '??').split(' ').map(n => n[0]).join('').slice(0, 2);
  const bg = COLORS[(name || '').length % COLORS.length];
  return <div className="avatar" style={{ width: size, height: size, background: bg, fontSize: size * 0.35 }}>{initials}</div>;
};

// Badge
const BADGE_MAP = {
  PENDING: 'badge-pending', APPROVED: 'badge-approved', REJECTED: 'badge-rejected',
  PROCESSED: 'badge-ordered', ORDERED: 'badge-ordered', DELIVERED: 'badge-delivered',
  PLANNED: 'badge-pending', CONFIRMED: 'badge-approved', IN_PROGRESS: 'badge-ordered', POSTPONED: 'badge-pending', COMPLETED: 'badge-delivered', CANCELLED: 'badge-rejected',
};
const BADGE_LABELS = {
  PENDING: 'Oczekuje', APPROVED: 'Zatwierdzony', REJECTED: 'Odrzucony',
  PROCESSED: 'W Enovie', ORDERED: 'Zamówione', DELIVERED: 'Dostarczone',
  PLANNED: 'Zaplanowany', CONFIRMED: 'Potwierdzony', COMPLETED: 'Zrealizowany',
  POSTPONED: 'Przełożony', IN_PROGRESS: 'W trakcie', CANCELLED: 'Anulowany',
  NORMAL: 'Normalny', URGENT: 'Pilne',
};
export const Badge = ({ status }) => (
  <span className={`badge ${BADGE_MAP[status] || 'badge-pending'}`}>
    {BADGE_LABELS[status] || status}
  </span>
);

// Person cell
export const PersonCell = ({ name }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
    <Avatar name={name} size={24} />
    <span style={{ whiteSpace: 'nowrap' }}>{name}</span>
  </span>
);

// Modal
// Kliknięcie w tło zamyka okno tylko wtedy, gdy wciśnięcie I puszczenie przycisku
// nastąpiły na tle. Samo `onClick` na tle odpalało się też, gdy użytkownik zaczął
// zaznaczać tekst w polu i puścił mysz poza oknem — i kasowało wpisane dane.
/**
 * Okno modalne (T7 + T62).
 *  - zamyka: ✕, Esc, kliknięcie w tło (wciśnięcie I puszczenie na tle),
 *  - `dirty` = formularz ma niezapisane zmiany → zamiast zamknięcia pytanie
 *    „Porzucić niezapisane zmiany?" [Porzuć / Wróć do edycji]; Esc i ✕ przy otwartym
 *    pytaniu wracają do edycji, Enter porzuca.
 */
/**
 * T165 (03.11): wybór godziny — godzina + minuty co 15 (00/15/30/45), zamiast pola z dokładnością do minuty.
 * onChange dostaje { target: { value: 'GG:MM' } } jak zwykłe pole. Nietypowa minuta z istniejącego wpisu zostaje do wyboru.
 */
export const TimeSelect = ({ value, onChange, allowEmpty = true, style }) => {
  const [h, m] = (value || '').split(':');
  const emit = (hh, mm) => onChange({ target: { value: hh === '' ? '' : `${hh}:${mm || '00'}` } });
  const mins = ['00', '15', '30', '45'];
  if (m && !mins.includes(m)) mins.push(m);
  const sel = { fontSize: 13, padding: '6px 4px' };
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', ...style }}>
      <select value={h || ''} onChange={e => emit(e.target.value, m)} style={sel} aria-label="godzina">
        {allowEmpty && <option value="">--</option>}
        {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(x => <option key={x} value={x}>{x}</option>)}
      </select>
      :
      <select value={m || '00'} onChange={e => emit(h || '00', e.target.value)} disabled={!h} style={sel} aria-label="minuty">
        {mins.sort().map(x => <option key={x} value={x}>{x}</option>)}
      </select>
    </span>
  );
};

// size: 'xl' — szerokie okno (formularz eventu); maximizable — przycisk „pełny ekran” (T169)
export const Modal = ({ title, onClose, children, wide, size, maximizable, dirty = false }) => {
  const [maxed, setMaxed] = React.useState(false);
  const pressedOnOverlay = React.useRef(false);
  const releasedOnOverlay = React.useRef(false);
  const [confirming, setConfirming] = React.useState(false);

  const tryClose = React.useCallback(() => {
    if (dirty) setConfirming(true); else onClose();
  }, [dirty, onClose]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); if (confirming) setConfirming(false); else tryClose(); }
      else if (e.key === 'Enter' && confirming) { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirming, tryClose, onClose]);

  return (
    <div className="modal-overlay"
      onMouseDown={e => { pressedOnOverlay.current = e.target === e.currentTarget; }}
      onMouseUp={e => { releasedOnOverlay.current = e.target === e.currentTarget; }}
      onClick={() => {
        if (pressedOnOverlay.current && releasedOnOverlay.current) tryClose();
        pressedOnOverlay.current = false;
        releasedOnOverlay.current = false;
      }}>
      <div className={`modal ${maxed ? 'modal-full' : size === 'xl' ? 'modal-xl' : wide ? 'modal-lg' : 'modal-sm'}`}>
        <div className="flex justify-between items-center" style={{ marginBottom: 14, gap: 8 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700 }}>{title}</h3>
          {maximizable && <button onClick={() => setMaxed(x => !x)} title={maxed ? 'Zmniejsz okno' : 'Pełny ekran'} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #E2E8F0', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#475569', padding: '2px 8px' }}>{maxed ? '⤡ Zmniejsz' : '⤢ Pełny ekran'}</button>}
          <button onClick={tryClose} title="Zamknij (Esc)" style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#94A3B8' }}>✕</button>
        </div>
        {/* T75: formularz ZAWSZE zostaje zamontowany (ukryty pod pytaniem) — inaczej formularze
            trzymające dane w sobie („Dodaj pracownika", profil) traciły wszystko po „Wróć do edycji" */}
        {confirming && (
          <div style={{ padding: '4px 0 14px', marginBottom: 12, borderBottom: '1px solid #E2E8F0' }}>
            <div style={{ fontSize: 14, marginBottom: 6 }}>Porzucić niezapisane zmiany?</div>
            <div style={{ fontSize: 11, color: '#64748B', marginBottom: 14 }}>Wpisane dane przepadną. Enter = porzuć, Esc = wróć do edycji.</div>
            <div className="flex gap-6 justify-end">
              <button className="btn btn-outline" onClick={() => setConfirming(false)}>Wróć do edycji</button>
              <button className="btn btn-danger" onClick={onClose} autoFocus>Porzuć zmiany</button>
            </div>
          </div>
        )}
        <div style={confirming ? { display: 'none' } : undefined}>{children}</div>
      </div>
    </div>
  );
};

/**
 * Przełącznik „Moje" / „Zespół" (T68/T49) — u osób, które widzą też cudze wpisy.
 * W „Moje" pokazujemy kafelki i sumy (tylko własne), w „Zespół" — zatwierdzanie.
 */
export const ScopeTabs = ({ scope, onChange, mineCount, teamCount, teamLabel = 'Zespół' }) => (
  <div className="filter-pills">
    <button className={`filter-pill ${scope === 'mine' ? 'active' : ''}`} onClick={() => onChange('mine')}>
      Moje{mineCount !== undefined ? ` (${mineCount})` : ''}
    </button>
    <button className={`filter-pill ${scope === 'team' ? 'active' : ''}`} onClick={() => onChange('team')}>
      {teamLabel}{teamCount !== undefined ? ` (${teamCount})` : ''}
    </button>
  </div>
);

/** T85: wpis dodany później niż N dni po dniu pracy — zatwierdzający ma to widzieć. */
export const LATE_ENTRY_DAYS = 3;
export const addedLate = (workDate, createdAt) => {
  if (!workDate || !createdAt) return false;
  const w = new Date(String(workDate).slice(0, 10));
  const c = new Date(String(new Date(createdAt).toISOString()).slice(0, 10));
  return (c - w) / 86400000 > LATE_ENTRY_DAYS;
};
export const fmtDay = (d) => d ? new Date(d).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' }) : '';
/** „Dodano 14.10" z kolorem, gdy spóźnione (T85). */
export const AddedOn = ({ date, createdAt }) => {
  if (!createdAt) return null;
  const late = addedLate(date, createdAt);
  return (
    <span style={{ fontSize: 10, color: late ? '#D97706' : '#94A3B8', fontWeight: late ? 700 : 400 }}
      title={late ? `Dodano ponad ${LATE_ENTRY_DAYS} dni po dniu pracy` : 'Data dodania wpisu'}>
      {late ? '⚠ ' : ''}dodano {fmtDay(createdAt)}
    </span>
  );
};
/** T79/T84: kto rozpatrzył wpis — „✓ Anna Kowalska, 14.10" / „⏳ czeka" / „✗ Anna K." */
/** T148: powód odrzucenia — pytamy przy ✗ (null = anulowano, '' = bez powodu). */
export const askRejectReason = () => {
  const r = window.prompt('Odrzucić? Wpisz powód — pracownik go zobaczy i poprawi wpis (np. „godziny jak reszta składu: 10:00–20:00”). Można zostawić puste.');
  return r === null ? null : r.trim();
};

/** T148: rzucająca się w oczy informacja u autora — wpis odrzucony, trzeba poprawić. */
export const RejectedNote = ({ item, onFix, what = 'Godziny' }) => (
  <div style={{ fontSize: 11, background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: 6, padding: '4px 8px', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
    <span>❌ <b>{what} odrzucone</b>{item.approvedBy ? ' (' + item.approvedBy.firstName + ' ' + (item.approvedBy.lastName || '') + ')' : ''}{item.rejectReason ? <>: „{item.rejectReason}”</> : ''}</span>
    {onFix && <button className="btn btn-danger btn-sm" style={{ fontSize: 10, padding: '1px 8px' }} onClick={onFix}>✎ Popraw i wyślij ponownie</button>}
  </div>
);

export const ReviewedBy = ({ item, short }) => {
  if (!item) return null;
  if (item.status === 'PENDING') return <span style={{ fontSize: 10, color: '#94A3B8' }}>⏳ czeka na zatwierdzenie</span>;
  const who = item.approvedBy ? `${item.approvedBy.firstName} ${short ? (item.approvedBy.lastName || '').slice(0, 1) + '.' : item.approvedBy.lastName}` : '—';
  const ok = item.status === 'APPROVED';
  return (
    <span style={{ fontSize: 10, color: ok ? '#059669' : '#DC2626' }} title={ok ? 'Zatwierdzone — zablokowane. Zmiana tylko po cofnięciu zatwierdzenia (zatwierdzający albo admin).' : undefined}>
      {ok ? '🔒 ✓' : '✗'} {who}{item.approvedAt ? `, ${fmtDay(item.approvedAt)}` : ''}
      {item.settlementId || item.settlement ? <span style={{ color: '#7C3AED' }}> · 💰 wypłacone</span> : null}
      {item.status === 'REJECTED' && item.rejectReason && <span style={{ display: 'block' }}>„{item.rejectReason}”</span>}
    </span>
  );
};

/**
 * T99: ⚠ przy wpisie, którego godziny odbiegają od reszty składu eventu. `tc` = teamCompare z API
 * albo wynik `teamDiffs` (szczegóły eventu).
 */
export const TeamDiffBadge = ({ tc }) => {
  if (!tc || !tc.differs) return null;
  const txt = tc.majority
    ? `Reszta składu: ${tc.majority.hours} h${tc.majority.time ? ` (${tc.majority.time})` : ''} — ${tc.majority.people} z ${tc.count} os.`
    : `W składzie różne godziny: ${tc.values.map(v => v + ' h').join(', ')} (${tc.count} os.)`;
  return <div style={{ fontSize: 10, color: '#B45309', fontWeight: 600 }} title="Przeważnie cała grupa ma tyle samo godzin — sprawdź przed zatwierdzeniem">⚠ inaczej niż skład · {txt}</div>;
};
/** T99: to samo liczone w przeglądarce dla listy wpisów jednego eventu (szczegóły eventu). id → teamCompare */
export const teamDiffs = (entries) => {
  const list = (entries || []).filter(x => x.workType !== 'EVENT_DAILY' && x.status !== 'REJECTED');
  const out = {};
  if (list.length < 2) return out;
  const byH = {}, times = {};
  for (const x of list) {
    const h = Number(x.totalHours);
    byH[h] = (byH[h] || 0) + 1;
    const t = x.startTime ? `${x.startTime}–${x.endTime || '?'}` : '';
    (times[h] = times[h] || {})[t] = ((times[h] || {})[t] || 0) + 1;
  }
  const values = Object.keys(byH).map(Number).sort((a, b) => a - b);
  if (values.length < 2) return out;
  const max = Math.max(...Object.values(byH));
  const modes = values.filter(v => byH[v] === max);
  const majority = modes.length === 1
    ? { hours: modes[0], time: Object.keys(times[modes[0]]).sort((a, b) => times[modes[0]][b] - times[modes[0]][a])[0] || '', people: max }
    : null;
  for (const x of list) out[x.id] = { count: list.length, majority, values, differs: !majority || Number(x.totalHours) !== majority.hours };
  return out;
};

/** T98: miesiąc magazynowego na etacie — norma, przepracowane, nadgodziny (dzienne/nocne) i kwota. */
export const MONTHS_PL = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];
export const NormCard = ({ s }) => {
  if (!s || !s.usesNorm) return null;
  const [y, m] = s.month.split('-').map(Number);
  const zl = (v) => `${(Number(v) || 0).toFixed(2).replace('.', ',')} zł`;
  return (
    <div className="card" style={{ border: '1px solid #BFDBFE', background: '#F8FAFC' }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>🏭 {MONTHS_PL[m - 1]} {y} — norma i nadgodziny</div>
      <div className="flex gap-8 flex-wrap" style={{ fontSize: 12, alignItems: 'baseline' }}>
        <span>Norma: <b>{s.norm} h</b> <span style={{ color: '#94A3B8' }}>({s.workdays} dni rob.{s.leaveDays ? ` − ${s.leaveDays} dni urlopu/L4` : ''} × 8 h)</span></span>
        <span>Dzienne: <b>{s.worked} h</b>{s.pendingHours > 0 && <span style={{ color: '#94A3B8' }}> + {s.pendingHours} h czeka</span>}</span>
        {s.overtimeDay > 0
          ? <span style={{ color: '#059669' }}>Nadgodziny dzienne: <b>{s.overtimeDay} h</b></span>
          : <span style={{ color: '#64748B' }}>Do normy brakuje: <b>{s.toNorm} h</b></span>}
        {s.nightHours > 0 && <span style={{ color: '#7C3AED' }}>Nocne: <b>{s.nightHours} h</b></span>}
        {(s.amount > 0 || s.pendingAmount > 0) && <span>Do wypłaty: <b style={{ color: '#059669' }}>{zl(s.amount)}</b>{s.pendingAmount > 0 && <span style={{ color: '#94A3B8' }}> + {zl(s.pendingAmount)} czeka</span>}</span>}
      </div>
      <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 4 }}>Godziny dzienne w normie są w pensji (wypłaca księgowość). Aplikacja płaci dzienne ponad normę miesiąca (stawka magazyn) i wszystkie godziny nocne (stawka nocna). Eventy liczone osobno.</div>
    </div>
  );
};

/** Czy formularz różni się od stanu początkowego (do `dirty` w Modal). */
export const isDirty = (current, initial) => JSON.stringify(current) !== JSON.stringify(initial);

// Toggle
/** T132: podpowiedź po najechaniu myszką (od razu po najechaniu — CSS .hint-wrap w global.css); na telefonie po dotknięciu. */
export const Hint = ({ text, children }) => text ? (
  <span className="hint-wrap" tabIndex={0}>
    {children}<span className="hint-dot" aria-hidden="true">i</span>
    <span className="hint-pop" role="tooltip">{text}</span>
  </span>
) : children;

export const Toggle = ({ on, onChange, label, hint }) => (
  <div className="flex justify-between items-center" style={{ padding: '5px 0' }}>
    <span style={{ fontSize: 12, color: '#334155' }}><Hint text={hint}>{label}</Hint></span>
    <div className={`toggle ${on ? 'toggle-on' : 'toggle-off'}`} onClick={() => onChange && onChange(!on)}>
      <div className="toggle-knob" style={{ left: on ? 19 : 3 }} />
    </div>
  </div>
);

// Date filter — `children` to miejsce na dodatkowe filtry (np. po pracowniku)
const QUICK_LABELS = { today: 'Dziś', week: 'Tydzień', month: 'Miesiąc', year: 'Rok' };
export const DateFilter = ({ from, to, onFromChange, onToChange, onFilter, quickFilter, onQuickFilter, children }) => (
  <div className="date-filter">
    <div className="input-group">
      <label>Od</label>
      <input type="date" value={from} onChange={e => onFromChange(e.target.value)} style={{ padding: '4px 7px', fontSize: 12 }} />
    </div>
    <div className="input-group">
      <label>Do</label>
      <input type="date" value={to} onChange={e => onToChange(e.target.value)} style={{ padding: '4px 7px', fontSize: 12 }} />
    </div>
    <button className="btn btn-outline btn-sm" onClick={onFilter}>Filtruj</button>
    <div className="flex gap-4">
      {Object.keys(QUICK_LABELS).map(p => (
        <button key={p} className={`quick-btn ${quickFilter === p ? 'active' : ''}`}
          onClick={() => onQuickFilter(p)}>
          {QUICK_LABELS[p]}
        </button>
      ))}
    </div>
    {children}
  </div>
);

// Filtr po pracowniku. Lista osób budowana z załadowanych wierszy (uniqueUsers),
// więc pokazuje tylko tych, którzy mają coś w wybranym okresie — bez osobnego zapytania.
export const uniqueUsers = (users) => {
  const byId = {};
  users.forEach(u => { if (u?.id && !byId[u.id]) byId[u.id] = u; });
  return Object.values(byId).sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'pl'));
};
export const EmployeeFilter = ({ people, value, onChange }) => (
  <div className="input-group">
    <label>Pracownik</label>
    <select value={value} onChange={e => onChange(e.target.value)} style={{ padding: '4px 7px', fontSize: 12, minWidth: 140 }}>
      <option value="">Wszyscy</option>
      {people.map(u => <option key={u.id} value={u.id}>{u.lastName} {u.firstName}</option>)}
    </select>
  </div>
);

// Sortowanie tabel po kliknięciu w nagłówek.
// `getters` mapuje klucz kolumny na funkcję wyciągającą wartość z wiersza —
// zdefiniować raz poza komponentem, żeby nie zmieniać się przy każdym renderze.
export const useSort = (rows, getters, defaultKey, defaultDir = 'desc') => {
  const [sort, setSort] = useState({ key: defaultKey, dir: defaultDir });
  const toggle = (key) => setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  const sorted = useMemo(() => {
    const get = getters[sort.key] || (r => r[sort.key]);
    const list = [...rows];
    list.sort((a, b) => {
      const va = get(a), vb = get(b);
      let c;
      if (va == null && vb == null) c = 0;
      else if (va == null) c = 1;
      else if (vb == null) c = -1;
      else if (typeof va === 'number' && typeof vb === 'number') c = va - vb;
      else c = String(va).localeCompare(String(vb), 'pl');
      return sort.dir === 'asc' ? c : -c;
    });
    return list;
  }, [rows, getters, sort]);
  return { sorted, sort, toggle };
};
export const SortTh = ({ label, k, sort, onSort }) => {
  const active = sort.key === k;
  return (
    <th onClick={() => onSort(k)} title="Kliknij, aby sortować"
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', color: active ? '#2563EB' : undefined, textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
      {label} <span style={{ color: active ? '#2563EB' : '#94A3B8', fontSize: 10 }}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </th>
  );
};

// Stat card
export const StatCard = ({ label, value, sub, accent }) => (
  <div className="card stat-card">
    <div className="stat-label">{label}</div>
    <div className="stat-value" style={{ color: accent || '#1E293B' }}>{value}</div>
    {sub && <div className="stat-sub">{sub}</div>}
  </div>
);

// Notification
export const Toast = ({ message, type = 'success', onClose }) => (
  <div style={{
    position: 'fixed', bottom: 20, right: 20, zIndex: 2000,
    padding: '10px 16px', borderRadius: 8,
    background: type === 'success' ? '#059669' : type === 'error' ? '#EF4444' : '#F59E0B',
    color: '#fff', fontSize: 13, fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: 8,
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    animation: 'modalIn 0.2s ease-out',
  }}>
    {message}
    <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14 }}>✕</button>
  </div>
);

// Custom hook for toast
export const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = (message, type = 'success') => {
    setToast({ message, type });
    // Błędy (np. za duży plik) wiszą dłużej — 6 s, żeby dało się je przeczytać; sukcesy 3 s
    setTimeout(() => setToast(null), type === 'error' ? 6000 : 3000);
  };
  const ToastComponent = toast ? <Toast {...toast} onClose={() => setToast(null)} /> : null;
  return { show, ToastComponent };
};

// Date helpers
export const useDateFilter = (initial = 'month') => {
  const now = new Date();
  const mon = new Date(now); mon.setDate(now.getDate() - now.getDay() + 1);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const yearRange = [`${now.getFullYear()}-01-01`, `${now.getFullYear()}-12-31`];
  const monthRange = [firstOfMonth.toISOString().split('T')[0], lastOfMonth.toISOString().split('T')[0]];
  const [from, setFrom] = useState(initial === 'year' ? yearRange[0] : monthRange[0]);
  const [to, setTo] = useState(initial === 'year' ? yearRange[1] : monthRange[1]);
  const [quick, setQuick] = useState(initial);

  const handleQuick = (q) => {
    setQuick(q);
    const today = new Date().toISOString().split('T')[0];
    if (q === 'today') { setFrom(today); setTo(today); }
    else if (q === 'week') { setFrom(mon.toISOString().split('T')[0]); setTo(sun.toISOString().split('T')[0]); }
    else if (q === 'year') { setFrom(`${now.getFullYear()}-01-01`); setTo(`${now.getFullYear()}-12-31`); }
    else { setFrom(firstOfMonth.toISOString().split('T')[0]); setTo(lastOfMonth.toISOString().split('T')[0]); }
  };

  return { from, to, setFrom, setTo, quick, handleQuick, params: `from=${from}&to=${to}` };
};

export const formatDate = (d) => new Date(d).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
export const formatDateFull = (d) => new Date(d).toLocaleDateString('pl-PL');
export const fullName = (u) => u ? `${u.firstName} ${u.lastName}` : '';

// Słowniki nazw — jedno miejsce, importować wszędzie (T12)
export const WORK_LABELS = {
  WAREHOUSE: 'Magazyn', EVENT: 'Event', NIGHT_WAREHOUSE: 'Nocna mag.',
  NIGHT_EVENT: 'Nocna event', EVENT_DAILY: 'Dniówka',
};
// Typy pracy eventowej: bez przerwy; dniówka to sztuka, nie godzina (T41, T39).
// To samo co EVENT_TYPES w backend/routes/hours.js.
export const EVENT_WORK_TYPES = ['EVENT', 'NIGHT_EVENT', 'EVENT_DAILY'];
export const BONUS_LABELS = {
  CONTAINER: 'Rozpakowanie kontenera', DISCRETIONARY: 'Premia uznaniowa',
  TRANSPORT: 'Transport', EXPENSE: 'Wyłożone ze swoich', OTHER: 'Inne',
  OVERTIME: 'Nadgodziny zlecone (wycofane)', // stare wpisy; nie do wyboru (T42)
};
// Do wyboru w formularzu, w tej kolejności — lustro PICKABLE_TYPES w backend/routes/bonuses.js
export const BONUS_PICKABLE = ['CONTAINER', 'DISCRETIONARY', 'TRANSPORT', 'EXPENSE', 'OTHER'];
export const BONUS_NEEDS_DESC = ['EXPENSE', 'OTHER'];
// T151: kto nie wpisuje godzin — sam zgłasza tylko te (lustro EXPENSE_ONLY_TYPES w routes/bonuses.js)
export const EXPENSE_ONLY_TYPES = ['EXPENSE', 'TRANSPORT', 'OTHER'];
export const BONUS_MIN_DESC = 10;
// T88: górna granica jednego dodatku (lustro MAX_AMOUNT w backend/routes/bonuses.js)
export const BONUS_MAX_AMOUNT = 2000;

/**
 * Podsumowanie wpisów godzin: godziny (bez dniówek) + kwota, dniówki (sztuki) + kwota,
 * razem. Odrzucone pominięte. Używane na Godzinach i Dashboardzie (T39).
 */
export const summarizeHours = (hours) => hours.reduce((acc, h) => {
  if (h.status === 'REJECTED') return acc;
  const amt = Number(h.totalAmount) || 0;
  if (h.workType === 'EVENT_DAILY') { acc.daily += 1; acc.dailyAmt += amt; }
  else { acc.hours += Number(h.totalHours) || 0; acc.hoursAmt += amt; }
  if (h.status === 'PENDING') acc.pending += 1;
  acc.total += amt;
  return acc;
}, { hours: 0, hoursAmt: 0, daily: 0, dailyAmt: 0, total: 0, pending: 0 });
export const LEAVE_LABELS = {
  VACATION: 'Wypoczynkowy', ON_DEMAND: 'Na żądanie', OCCASIONAL: 'Okolicznościowy',
  UNPAID: 'Bezpłatny', CHILDCARE: 'Opieka nad dzieckiem (art. 188)',
  CARE_ALLOWANCE: 'Opieka nad chorym (zasiłek)', REMOTE_WORK: 'Praca zdalna', PATERNITY: 'Ojcowski',
  MATERNITY: 'Macierzyński', PARENTAL: 'Rodzicielski', SICK: 'Zwolnienie lekarskie',
};
export const leaveLabel = (t) => LEAVE_LABELS[t] || t;
// T142: urlop godzinowy — saldo w dniach może mieć ułamek (2 h = 0,25 dnia) → „20 dni 6 h”
export const fmtLeaveDays = (d) => {
  const h = Math.round((Number(d) || 0) * 8);
  const days = Math.floor(h / 8), rest = h % 8;
  return rest ? `${days} dni ${rest} h` : `${days}`;
};
/** Pozostała pula z konta: (przysługuje − wykorzystane) z godzinami poza pełnymi dniami */
export const leaveLeft = (u) => (u?.leaveDaysTotal || 0) - (u?.leaveDaysUsed || 0) - (u?.leaveHoursUsed || 0) / 8;
/** Długość wniosku: „2 h” albo „3 dni” */
export const leaveLength = (l) => l?.hours ? `${l.hours} h` : `${l?.totalDays} ${l?.totalDays === 1 ? 'dzień' : 'dni'}`;
// T130: kto z zespołu jest nieobecny w czasie oczekującego wniosku (pole overlap z GET /leaves)
const d2 = (k) => `${k.slice(8, 10)}.${k.slice(5, 7)}`;
export const overlapShort = (o) => `${o.people.length === 1 ? '1 os.' : `${new Set(o.people.map(p => p.name)).size} os.`} ${o.sameTeam ? 'z zespołu' : 'z tej samej roli'} też nieobecn${o.people.length === 1 ? 'a' : 'e'}`;
export const overlapText = (o) => `W tym czasie nieobecni ${o.sameTeam ? 'z tego samego zespołu' : 'z tej samej roli'}:\n` +
  o.people.map(p => `• ${p.name} — ${leaveLabel(p.leaveType)} ${d2(p.startDate)}–${d2(p.endDate)}${p.status === 'PENDING' ? ' (czeka)' : ''}`).join('\n') +
  (o.peak >= 2 ? `\n\nNajwięcej jednego dnia: ${o.peak} os. (${d2(o.peakDay)}).` : '');
// Typy, które schodzą z puli 26/20 dni — to samo co POOL_TYPES w backend/routes/leaves.js
export const POOL_LEAVE_TYPES = ['VACATION', 'ON_DEMAND'];
export const workLabel = (t) => WORK_LABELS[t] || t;
export const bonusLabel = (t) => BONUS_LABELS[t] || t;

// Role labels
export const ROLE_LABELS = {
  // Jedno miejsce na nazwy rol - importowac stad, nie kopiowac (B0.1).
  // "Biuro" zamiast "Administracja", zeby nie mylilo sie z Adminem (B0.4);
  // nazwa roli w bazie (ADMINISTRATION) bez zmian.
  ADMIN: 'Admin', COORDINATOR: 'Koordynator', ADMINISTRATION: 'Biuro',
  // T106 (13.10): rola WAREHOUSE w interfejsie = „Fizyczny” (magazynier, spawacz, tapicer…); w bazie bez zmian
  WAREHOUSE: 'Fizyczny', EVENT: 'Eventowy',
};
export const ROLE_COLORS = {
  ADMIN: '#7C3AED', COORDINATOR: '#2563EB', ADMINISTRATION: '#0891B2',
  WAREHOUSE: '#059669', EVENT: '#D97706',
};
export const RoleBadge = ({ role }) => (
  <span style={{ padding: '1px 6px', borderRadius: 99, fontSize: 9, fontWeight: 700, background: (ROLE_COLORS[role] || '#94A3B8') + '18', color: ROLE_COLORS[role] || '#64748B' }}>
    {ROLE_LABELS[role] || role}
  </span>
);
